import * as THREE from 'three';
import { Character } from '../Character';
import { PedestrianBehavior } from './PedestrianBehavior';

export class HeadLookAt {
    private static readonly DETECTION_RADIUS = 3;
    private static readonly FOV_DOT_THRESHOLD = 0; // cos(90°) — ignore anything behind
    private static readonly MAX_H_ANGLE = Math.PI / 3; // ±60°
    private static readonly MAX_V_ANGLE = Math.PI / 9; // ±20°
    private static readonly SPEED_IN = 5;
    private static readonly SPEED_OUT = 3;
    private static readonly HEAD_BONE = 'Head';
    private static readonly TARGET_HEIGHT = 0.7;

    private headBone: THREE.Object3D | null = null;
    private weight = 0;
    // Head bone's local quaternion at GLTF bind time, saved before any animation runs.
    // Used as the neutral rotation once the mixer no longer drives the head bone.
    private readonly bindQuat = new THREE.Quaternion();
    // Last known target position in world space — stable across animation loop resets.
    private readonly lastTargetWorldPos = new THREE.Vector3();

    constructor(private readonly character: Character) {
        character.traverse((obj) => {
            if (!this.headBone && obj.name === HeadLookAt.HEAD_BONE) {
                this.headBone = obj;
                this.bindQuat.copy(obj.quaternion);
            }
        });
    }

    public update(timeStep: number): void {
        if (!this.headBone || !this.headBone.parent) { return; }

        const targetPos = this.findTargetPos();

        if (targetPos) {
            this.lastTargetWorldPos.copy(targetPos);
            this.weight = Math.min(1, this.weight + HeadLookAt.SPEED_IN * timeStep);
        } else {
            this.weight = Math.max(0, this.weight - HeadLookAt.SPEED_OUT * timeStep);
        }

        if (this.weight <= 0) {
            // Restore bind pose — the mixer no longer does this for us.
            this.headBone.quaternion.copy(this.bindQuat);
            return;
        }

        // Neutral world orientation = parent animation pose * head bind-pose local quat.
        // Because the head bone has no animation track of its own, this is perfectly stable
        // across animation loop resets — no snaps from idle-animation discontinuities.
        const parentWorldQuat = this.headBone.parent.getWorldQuaternion(new THREE.Quaternion());
        const neutralWorldQuat = parentWorldQuat.clone().multiply(this.bindQuat);

        const headWorldPos = this.headBone.getWorldPosition(new THREE.Vector3());
        const lookDir = this.lastTargetWorldPos.clone().sub(headWorldPos).normalize();
        const headForward = new THREE.Vector3(0, 0, 1).applyQuaternion(neutralWorldQuat);

        const rawDelta = new THREE.Quaternion().setFromUnitVectors(headForward, lookDir);
        const euler = new THREE.Euler().setFromQuaternion(rawDelta, 'YXZ');
        euler.y = Math.max(-HeadLookAt.MAX_H_ANGLE, Math.min(HeadLookAt.MAX_H_ANGLE, euler.y));
        euler.x = Math.max(-HeadLookAt.MAX_V_ANGLE, Math.min(HeadLookAt.MAX_V_ANGLE, euler.x));
        euler.z = 0;

        const targetWorldQuat = new THREE.Quaternion().setFromEuler(euler).multiply(neutralWorldQuat);
        const finalWorldQuat = neutralWorldQuat.clone().slerp(targetWorldQuat, this.weight);
        this.headBone.quaternion.copy(parentWorldQuat.inverse().multiply(finalWorldQuat));
    }

    private findTargetPos(): THREE.Vector3 | null {
        if (this.character.isDead || !(this.character.behaviour instanceof PedestrianBehavior)) {
            return null;
        }

        const player = this.character.world?.player;
        if (!player) { return null; }

        const targetPos = player.getWorldPosition(new THREE.Vector3());
        const npcPos = this.character.getWorldPosition(new THREE.Vector3());

        if (targetPos.distanceTo(npcPos) > HeadLookAt.DETECTION_RADIUS) { return null; }

        targetPos.y += HeadLookAt.TARGET_HEIGHT;

        const toPlayer = targetPos.clone().sub(npcPos).setY(0).normalize();
        const npcForward = new THREE.Vector3(0, 0, 1).applyQuaternion(this.character.quaternion);
        if (npcForward.dot(toPlayer) <= HeadLookAt.FOV_DOT_THRESHOLD) {
            return null;
        }

        return targetPos;
    }
}
